import { useEffect, useState } from 'react';
import { Outlet, useNavigate, NavLink } from 'react-router-dom';
import WebApp from '@twa-dev/sdk';
import { getMe } from '../../api/users.api';
import { useAdminConfig } from '../../hooks/useAdminConfig';
import { getTelegramInitData } from '../../utils/initData';
import AdminErrorBoundary from './AdminErrorBoundary';
import './admin.css';
import { ADMIN_PERMISSIONS } from '../../config/admin-permissions';

export default function AdminLayout() {
  const navigate = useNavigate();
  const { config: adminConfig, loading: adminConfigLoading, error: adminConfigError, reload: reloadAdminConfig } = useAdminConfig();
  const [status, setStatus] = useState<'loading' | 'forbidden' | 'ok'>('loading');
  const [forbiddenReason, setForbiddenReason] = useState<string>('');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // В Mini App при открытии админки запрашиваем полный экран (иначе может оставаться маленькое окно)
  useEffect(() => {
    try {
      if (typeof WebApp !== 'undefined' && WebApp.expand) {
        WebApp.expand();
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (!getTelegramInitData()) {
      setForbiddenReason('Откройте админ-панель из Telegram Mini App (в обычном браузере нет Telegram initData).');
      setStatus('forbidden');
      return;
    }
    getMe()
      .then((data) => {
        if (data.isAdmin) {
          setForbiddenReason('');
          setStatus('ok');
        } else {
          setForbiddenReason('Ваш Telegram-аккаунт не имеет доступа к админ-панели.');
          setStatus('forbidden');
        }
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : '';
        if (/init.?data|telegram|401|403|авторизац/i.test(msg)) {
          setForbiddenReason('Не удалось подтвердить Telegram-сессию. Откройте админ-панель из меню бота в Telegram.');
        } else {
          setForbiddenReason('Доступ запрещён или временно недоступен.');
        }
        setStatus('forbidden');
      });
  }, []);

  if (status === 'loading') {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
        Загрузка…
      </div>
    );
  }

  if (status === 'forbidden') {
    return (
      <div
        style={{
          minHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          color: 'var(--text-primary)',
        }}
      >
        <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>403</div>
        <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '20px' }}>
          {forbiddenReason || 'Доступ запрещён'}
        </div>
        <button
          type="button"
          onClick={() => navigate('/profile')}
          style={{
            padding: '12px 24px',
            borderRadius: '12px',
            border: 'none',
            backgroundColor: 'var(--color-accent)',
            color: 'var(--text-on-accent)',
            fontSize: '15px',
            cursor: 'pointer',
          }}
        >
          В профиль
        </button>
      </div>
    );
  }

  if (adminConfigLoading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
        Загружаем конфигурацию админ-панели…
      </div>
    );
  }

  if (!adminConfig) {
    return (
      <div
        style={{
          minHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          padding: '24px',
          color: 'var(--text-primary)',
        }}
      >
        <div style={{ fontSize: '18px', fontWeight: 600 }}>Не удалось открыть админ-панель</div>
        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', maxWidth: '380px', textAlign: 'center' }}>
          {adminConfigError || 'Сервер не вернул настройки доступа. Проверьте переменные ADMIN_* и доступ к /api/admin/config.'}
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={reloadAdminConfig}
            className="admin-btn admin-btn-primary"
          >
            Повторить
          </button>
          <button
            type="button"
            onClick={() => navigate('/profile')}
            className="admin-btn admin-btn-secondary"
          >
            В профиль
          </button>
        </div>
      </div>
    );
  }

  const isDark = typeof WebApp !== 'undefined' && WebApp.colorScheme === 'dark';
  const permissions = new Set(adminConfig.permissions ?? []);

  const navItems = [
    { to: '/admin/dashboard', label: 'Дашборд', icon: '◧', visible: permissions.has(ADMIN_PERMISSIONS.DASHBOARD_VIEW), enabled: !!adminConfig?.modules?.dashboard },
    { to: '/admin/catalog', label: 'Каталог', icon: '✿', visible: permissions.has(ADMIN_PERMISSIONS.CATALOG_VIEW), enabled: true },
    { to: '/admin/users', label: 'Пользователи', icon: '◉', visible: permissions.has(ADMIN_PERMISSIONS.USERS_VIEW), enabled: !!adminConfig?.modules?.users },
    { to: '/admin/export', label: 'Экспорт', icon: '⇩', visible: permissions.has(ADMIN_PERMISSIONS.EXPORT_VIEW), enabled: !!adminConfig?.modules?.export },
    { to: '/admin/forecast', label: 'Прогноз', icon: '⌁', visible: permissions.has(ADMIN_PERMISSIONS.FORECAST_VIEW), enabled: !!adminConfig?.modules?.forecast && adminConfig?.features?.forecast !== false },
    { to: '/admin/marketing', label: 'Источники', icon: '🔗', visible: permissions.has(ADMIN_PERMISSIONS.MARKETING_VIEW), enabled: !!adminConfig?.modules?.marketing },
    { to: '/admin/promocodes', label: 'Промокоды', icon: '🏷', visible: permissions.has(ADMIN_PERMISSIONS.PROMOCODES_VIEW), enabled: !!adminConfig?.modules?.promocodes },
    { to: '/admin/audit', label: 'Журнал', icon: '🧾', visible: permissions.has(ADMIN_PERMISSIONS.AUDIT_VIEW), enabled: true },
  ].filter((item) => item.visible);

  return (
    <div
      className="admin-layout-root"
      style={{ minHeight: '100vh', width: '100%', boxSizing: 'border-box', backgroundColor: 'var(--bg-main)' }}
      data-theme={isDark ? 'dark' : 'light'}
    >
      <div className="admin-topbar">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => navigate('/profile')}
            aria-label="Назад в профиль"
            className="admin-back-btn"
          >
            <span aria-hidden>←</span>
          </button>
          <div>
            <div className="admin-topbar-title">Панель управления</div>
            <div className="admin-topbar-subtitle">Управление админ-панелью</div>
          </div>
        </div>
        <button
          type="button"
          className="admin-btn admin-btn-secondary admin-mobile-nav-toggle"
          onClick={() => setMobileNavOpen((v) => !v)}
        >
          Разделы
        </button>
      </div>
      <nav className={`admin-nav-redesign ${mobileNavOpen ? 'is-open' : ''}`} aria-label="Разделы админ-панели">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end
            onClick={() => setMobileNavOpen(false)}
            className={({ isActive }) => `admin-nav-item ${isActive ? 'is-active' : ''} ${item.enabled ? '' : 'is-locked'}`}
          >
            <span className="admin-nav-item-icon" aria-hidden>
              {item.icon}
            </span>
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
      {mobileNavOpen && <button type="button" className="admin-nav-backdrop" onClick={() => setMobileNavOpen(false)} aria-label="Закрыть меню" />}
      <main
        className="admin-content"
        style={{
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <AdminErrorBoundary>
          <Outlet />
        </AdminErrorBoundary>
      </main>
    </div>
  );
}
